import { useState, useCallback, useEffect, useRef } from 'react'
import { Button, Modal } from 'react-bootstrap'

import { ReactComponent as CloseDuotoneSvg } from '../../../../assets/svg/close-duotone.svg'
import { ReactComponent as UserDuotoneSvg } from '../../../../assets/svg/user-duotone.svg'
import { ReactComponent as BankDuotoneSvg } from '../../../../assets/svg/bank-duotone.svg'
import { ReactComponent as CopyDuotoneSvg } from '../../../../assets/svg/copy-duotone.svg'
import { ReactComponent as HandArrowDownSvg } from '../../../../assets/svg/hand-arrow-down.svg'
import { ReactComponent as HandWavingDuotoneSvg } from '../../../../assets/svg/hand-waving-duotone.svg'
import { ReactComponent as UserFocusDuotoneSvg } from '../../../../assets/svg/user-focus-duotone.svg'
import { ReactComponent as MoneyWavyDuotoneSvg } from '../../../../assets/svg/money-wavy-duotone.svg'
import { ReactComponent as InfoDuotoneSvg } from '../../../../assets/svg/info-duotone.svg'
import ErrorToast from '../../../elements/v2/ErrorToast'
import { useSession } from '../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../contexts/session/DirectDebitContext'
import { SessionStatus } from '../../../../constants/SessionConstants'

const DIRECT_DEBIT_PAYMENT_METHOD = 'BANK_ACCOUNT_DIRECT_DEBIT'
const POLL_INTERVAL_MS = 20000

const formatAmount = (amount) => {
  const numericAmount = Number(amount)
  if (amount === null || amount === undefined || Number.isNaN(numericAmount)) return ''
  return `₦${numericAmount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const TransferConfirmationScreen = ({
  sessionKey,
  showSuccess,
  otpError,
  setOtpError,
}) => {
  const { loadSessionData } = useSession()
  const { transferDetails, showFailed, accountNumber, selectedBank } = useDirectDebitStatus()

  const [checking, setChecking] = useState(false)
  const [showWhyTransferModal, setShowWhyTransferModal] = useState(false)
  const resolvedRef = useRef(false)
  const checkInFlightRef = useRef(false)

  const directDebitDetail = transferDetails?.paymentMethodDetails?.find(
    (detail) => detail.paymentMethod === DIRECT_DEBIT_PAYMENT_METHOD
  )
  const activationData = directDebitDetail?.data || {}
  const activationAccountNumber = activationData.activationAccountNumber || ''
  const activationAccountName = activationData.activationAccountName || ''
  const activationBankName = activationData.activationBankName || ''
  const activationAmount = activationData.activationAmount || ''
  const displayAmount = formatAmount(transferDetails?.metadata?.authorizationAmount ?? activationAmount)

  const checkTransferStatus = useCallback(async (isManual = false) => {
    if (resolvedRef.current || checkInFlightRef.current) return
    checkInFlightRef.current = true
    if (isManual) setChecking(true)
    setOtpError('')

    try {
      const response = await loadSessionData(sessionKey)
      const status = response?.data?.status

      if (status === SessionStatus.SUCCESS) {
        resolvedRef.current = true
        showSuccess()
      } else if (status === SessionStatus.FAILED || status === SessionStatus.CANCELLED) {
        resolvedRef.current = true
        showFailed(response?.data?.message || response?.statusMessage || 'Your transfer could not be confirmed.')
      }
      // still pending: say nothing further - the button already reflects this
    } catch (error) {
      console.error('Error checking direct debit transfer status:', error)
      if (isManual) {
        setOtpError(error.message || 'An error occurred while checking the transfer status')
      }
    } finally {
      checkInFlightRef.current = false
      if (isManual) setChecking(false)
    }
  }, [sessionKey, loadSessionData, showSuccess, showFailed, setOtpError])

  const checkTransferStatusRef = useRef(checkTransferStatus)
  useEffect(() => {
    checkTransferStatusRef.current = checkTransferStatus
  }, [checkTransferStatus])

  useEffect(() => {
    checkTransferStatusRef.current(false)
    const interval = setInterval(() => checkTransferStatusRef.current(false), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  const handleManualCheck = useCallback(() => checkTransferStatus(true), [checkTransferStatus])

  return (
    <div
      style={{ height: '100%', padding: 'var(--dd-content-padding-y) var(--dd-content-padding-x)' }}
      className='d-flex flex-column justify-content-between'
    >
      <div>
        <div>
          <div
            style={{ width: '45px', height: '45px', background: '#F7F7F7' }}
            className='rounded-pill mx-auto d-flex align-items-center justify-content-center'
          >
            <HandArrowDownSvg width={24} />
          </div>

          <div>
            <p
              style={{ color: '#414141', fontSize: '12px' }}
              className='mt-3 mb-1 d-block'
            >
              Transfer{' '}
              <span style={{ color: '#231F20' }} className='fw-semibold'>
                {displayAmount}
              </span>{' '}
              to the account details below
            </p>
          </div>

          <Button
            variant='link'
            onClick={() => setShowWhyTransferModal(true)}
            style={{ fontSize: '12px', color: '#913D14' }}
            className='fw-semibold text-decoration-none h-auto p-0'
          >
            Why do i need to make this Transfer?
          </Button>

          <Modal
            centered
            show={showWhyTransferModal}
            onHide={() => setShowWhyTransferModal(false)}
            size='sm'
          >
            {/* A close button floating outside the modal's edge (the previous treatment) has
                nowhere to float to on mobile, where the dialog is nearly full-width - it was
                rendering half off-screen. Keeping it inside the header bounds fixes that at
                any viewport size. */}
            <Modal.Header
              style={{ padding: '16px' }}
              className='d-flex align-items-start justify-content-between gap-2'
            >
              <Modal.Title
                style={{ color: '#344054', minWidth: 0 }}
                className='small fw-semibold mb-0'
              >
                <HandWavingDuotoneSvg width={16} height={16} style={{ flexShrink: 0 }} />
                Why do i need to make this Transfer?
              </Modal.Title>

              <Button
                variant='light'
                className='rounded-pill bg-white p-0 flex-shrink-0'
                style={{
                  width: '32px',
                  height: '32px',
                  boxShadow: '0px 2px 4px 0px #00000026',
                }}
                onClick={() => setShowWhyTransferModal(false)}
              >
                <CloseDuotoneSvg width={16} height={16} />
              </Button>
            </Modal.Header>
            <Modal.Body
              style={{ padding: '8px 12px' }}
              color='#344054'
              className='small'
            >
              This Transfer as to be made so as to ensure that NiBSS recognizes
              your account for future transactions{' '}
            </Modal.Body>
          </Modal>
        </div>

        <div
          style={{
            background: '#F7F7F7',
            marginTop: '32px',
            marginBottom: '22px',
            padding: '8px 12px',
          }}
          className='rounded'
        >
          <div className='d-flex align-items-center justify-content-between py-1 px-2 mb-2'>
            <div className='d-flex align-items-center gap-2'>
              {' '}
              <MoneyWavyDuotoneSvg width={14} />{' '}
              <p
                style={{ color: '#515568', fontSize: '12px' }}
                className='fw-medium'
              >
                Subscription fee
              </p>
            </div>

            <p
              style={{ color: '#414141', fontSize: '12px' }}
              className='fw-medium'
            >
              {' '}
              {displayAmount}
            </p>
          </div>

          <div className='d-flex align-items-center justify-content-between py-1 px-2 mb-2'>
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              <UserFocusDuotoneSvg width={14} fill='' />{' '}
              <p
                style={{ color: '#515568', fontSize: '12px' }}
                className='fw-medium'
              >
                Account number
              </p>
            </div>

            {/* display:block + minWidth:0 override the global `p { display: flex }` rule and the
                default flex-item min-width:auto - without both, a long value pushes off-screen
                instead of shrinking/wrapping within the row (real bug on mobile with real data). */}
            <p
              style={{
                color: '#414141',
                fontSize: '12px',
                display: 'block',
                minWidth: 0,
                overflowWrap: 'break-word',
              }}
              className='fw-medium gap-2 text-end ms-2'
            >
              {activationAccountNumber} <CopyDuotoneSvg width={14} />
            </p>
          </div>

          <div className='d-flex align-items-center justify-content-between py-1 px-2 mb-2'>
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              <UserDuotoneSvg width={14} />{' '}
              <p
                style={{ color: '#515568', fontSize: '12px' }}
                className='fw-medium'
              >
                Account name
              </p>
            </div>

            <p
              style={{
                color: '#414141',
                fontSize: '12px',
                display: 'block',
                minWidth: 0,
                overflowWrap: 'break-word',
              }}
              className='fw-medium text-end text-uppercase ms-2'
            >
              {activationAccountName}
            </p>
          </div>

          <div className='d-flex align-items-center justify-content-between py-1 px-2 mb-2'>
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              {' '}
              <BankDuotoneSvg width={14} />{' '}
              <p
                style={{ color: '#515568', fontSize: '12px' }}
                className='fw-medium'
              >
                Bank name
              </p>
            </div>

            <p
              style={{
                color: '#414141',
                fontSize: '12px',
                display: 'block',
                minWidth: 0,
                overflowWrap: 'break-word',
              }}
              className='fw-medium text-end ms-2'
            >
              {activationBankName}
            </p>
          </div>
        </div>

        <div
          style={{ background: '#FEF0E9', padding: '12px 20px' }}
          className='rounded d-flex align-items-center gap-2'
        >
          <InfoDuotoneSvg width={14} style={{ flexShrink: 0 }} />
          <p
            style={{ color: '#913D14', fontSize: '12px', display: 'block', minWidth: 0, flex: '1 1 0%' }}
            className='fw-medium text-start mb-0'
          >
            {accountNumber && selectedBank?.bankName ? (
              <>
                Please make sure this one-time non-refundable transfer is sent from{' '}
                <span className='fw-semibold'>{selectedBank.bankName}</span> -{' '}
                <span className='fw-semibold'>{accountNumber}</span>
              </>
            ) : (
              'Please make sure this one-time non-refundable transfer is sent from the account you are trying to authorize'
            )}
          </p>
        </div>
      </div>

      <div>
        <ErrorToast message={otpError} className='mt-3' />

        <Button
          style={{ background: '#F26522', padding: '12px', marginTop: '24px' }}
          className='w-100 rounded-pill text-white border-0 fw-medium small'
          onClick={handleManualCheck}
          disabled={checking}
        >
          {checking ? 'Checking...' : 'I have made the transfer'}
        </Button>
      </div>
    </div>
  )
}

export default TransferConfirmationScreen
